import type { Logger as PinoLogger, TransportTargetOptions } from "pino";
import pino, { stdTimeFunctions } from "pino";
import type { PrettyOptions } from "pino-pretty";
import type { ILogger } from "./type";

const level = "trace";

const stdout = 1;

type ILoggerConfig = {
  output: "file" | "pretty";
  singleLine: boolean;
  enabled: boolean;
};

const getTarget = (config: ILoggerConfig): TransportTargetOptions => {
  if (config.output === "file") {
    return {
      target: "pino/file",
      options: { destination: stdout },
      level,
    };
  }

  const prettyOptions: PrettyOptions = {
    destination: stdout,
    colorizeObjects: true,
    colorize: true,
    singleLine: config.singleLine,
  };

  return {
    target: "pino-pretty",
    options: prettyOptions,
    level,
  };
};

const pinoLogger = (config: ILoggerConfig): PinoLogger => {
  return pino({
    transport: {
      targets: [getTarget(config)],
    },
    enabled: config.enabled,
    level,
    timestamp: stdTimeFunctions.isoTime,
    redact: {
      paths: [
        "context.headers.authorization",
        "context.headers.cookie",
        'context.headers["set-cookie"]',
        'context.headers["set-cookie"]',
      ],
    },
  });
};

class Logger implements ILogger {
  private pinoLogger: PinoLogger;

  private properties: Record<string, unknown>;

  private constructor(config: ILoggerConfig) {
    this.pinoLogger = pinoLogger(config);
    this.properties = {};
  }

  private static _instance: Logger;

  public static getInstance(config: ILoggerConfig): Logger {
    return this._instance || (this._instance = new this(config));
  }

  public addProperties(params: Record<string, unknown>): void {
    this.properties = params;
  }

  private parseObject(
    context: Record<string, unknown>,
  ): Record<string, unknown> {
    const trace: Record<string, unknown> = {};
    trace.business_id = context?.business_id ?? context?.businessId;
    trace.ordering_mode =
      context?.ordering_mode ??
      context?.orderingMode ??
      context?.orderingService ??
      context?.ordering_service;
    trace.outlet_id = context?.outlet_id ?? context?.outletId;

    return {
      trace: { ...trace, ...this.properties },
      context,
    };
  }

  public info(message: string, context: Record<string, unknown>): void {
    this.pinoLogger.info(this.parseObject(context), message);
  }

  public debug(message: string, context: Record<string, unknown>): void {
    this.pinoLogger.debug(this.parseObject(context), message);
  }

  public warning(message: string, context: Record<string, unknown>): void {
    this.pinoLogger.warn(this.parseObject(context), message);
  }

  public error(message: string, context: Record<string, unknown>): void {
    this.pinoLogger.error(this.parseObject(context), message);
  }
}

export default Logger;
